import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Layers, Library, FileText, Bot, ArrowRight, Route, Gavel } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Logo, Wordmark } from "@/components/Logo";
import OracleWidget from "@/components/OracleWidget";
import { useAuth } from "@/context/AuthContext";

const features = [
  { icon: Route, title: "Guided Study Path", desc: "Three tracks in a recommended order — Foundations, Status & Citizenship (Williams), and Consumer Empowerment (Hood) — so a beginner always knows the next step.", to: "/study-path" },
  { icon: BookOpen, title: "Structured Courses", desc: "In-depth courses on trusts, the Acts of Congress, the 'Person' treatise, citizenship, driving vs. traveling, contracts, FCRA/FDCPA/TILA and more — each with the real cases it cites.", to: "/courses" },
  { icon: Layers, title: "Flashcard Decks", desc: "Study core terminology across legal, banking, status and consumer law with a 3D flip deck and a self-testing quiz mode.", to: "/flashcards" },
  { icon: Library, title: "Legal & Banking Dictionaries", desc: "A searchable, A–Z glossary defining every key term — with accurate meanings and, where relevant, the contested theory's take.", to: "/dictionary" },
  { icon: Gavel, title: "Case Law Search Engine", desc: "Search millions of open U.S. court opinions, or ask a question and let the AI read the opinions and answer with citations.", to: "/caselaw" },
  { icon: Bot, title: "AI Legal Assistant", desc: "Ask questions and draft documents with an AI assistant powered by Claude Sonnet 4.6.", to: "/assistant" },
];

export default function Landing() {
  const { user } = useAuth();
  const cta = user ? "/dashboard" : "/register";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1535905557558-afc4877a26fc?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000')" }}
        />
        <div className="absolute inset-0 bg-background/85" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 border border-border px-3 py-1 mb-8">
              <Logo className="w-4 h-4" />
              <Wordmark className="text-xs uppercase tracking-widest font-mono text-muted-foreground" />
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.05]">
              <Wordmark /> — <span className="text-primary">a magnifying glass on the meaning of law.</span>
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-16 mb-4"
          >
            <OracleWidget variant="hero" />
          </motion.div>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
            {user && (
              <Link to="/dashboard" data-testid="hero-cta" className="text-sm text-primary hover:opacity-80 transition-opacity border-b border-transparent hover:border-primary pb-0.5">
                Go to Dashboard →
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-14">
          <span className="text-xs uppercase tracking-widest font-mono text-primary">The Archive</span>
          <h2 className="font-serif text-4xl mt-3">Five ways to master the material</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="bg-card p-8 group"
            >
              <Link to={f.to} data-testid={`feature-${i}`} className="block h-full">
                <f.icon className="w-7 h-7 text-primary mb-6" />
                <h3 className="font-serif text-2xl mb-3">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-mono text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  Enter <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            </motion.div>
          ))}
          <div className="bg-primary p-8 flex flex-col justify-center">
            <h3 className="font-serif text-2xl text-primary-foreground mb-3">Ready to go down the rabbit hole?</h3>
            <Link to={cta} className="text-sm text-primary-foreground/90 hover:text-primary-foreground inline-flex items-center gap-2">
              Become a member <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <p className="text-xs text-muted-foreground font-mono leading-relaxed max-w-4xl">
            <span className="text-primary uppercase tracking-widest">Disclaimer — </span>
            For educational purposes only. None of this shall be construed as legal advice.
          </p>
        </div>
      </footer>
    </div>
  );
}
